import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Mail } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { authRepository } from "@/data/repositories";
import type { Viewer } from "@/domain/types";
import { supabase } from "@/integrations/supabase/client";
import { vueltaPermitida } from "@/mcp/consentimiento";

/**
 * Permiso para el conector del CRM en la app de Claude.
 *
 * Supabase Auth (servidor OAuth 2.1) manda aquí con `?authorization_id=...`.
 * Juan entra con su cuenta del CRM y pulsa Permitir; Supabase devuelve a Claude
 * un código que cambia por el token. Solo pasa la cuenta del CRM y solo hacia
 * la dirección de vuelta de Claude: cualquier otra cosa se rechaza.
 */
export const Route = createFileRoute("/oauth/consent")({
  head: () => ({
    meta: [
      { title: "Conectar Claude — SUSIEDAD" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PantallaConsentimiento,
});

type Estado =
  | { paso: "cargando" }
  | { paso: "entrar" }
  | { paso: "pedir"; cliente: string; email: string; destino: string }
  | { paso: "rechazado"; motivo: string }
  | { paso: "error"; motivo: string };

function PantallaConsentimiento() {
  const [estado, setEstado] = useState<Estado>({ paso: "cargando" });
  const [enviando, setEnviando] = useState(false);
  const autorizacion =
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search).get("authorization_id");

  const cargar = useCallback(
    async (viewer: Viewer | null) => {
      if (!autorizacion)
        return setEstado({
          paso: "error",
          motivo: "Falta la petición de Claude. Vuelve a conectar desde la app.",
        });
      if (!viewer) return setEstado({ paso: "entrar" });
      if (!viewer.isAdmin) {
        await supabase.auth.oauth.denyAuthorization(autorizacion, { skipBrowserRedirect: true });
        return setEstado({ paso: "rechazado", motivo: "Esta cuenta no tiene acceso al CRM." });
      }
      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(autorizacion);
      if (error || !data)
        return setEstado({
          paso: "error",
          motivo: error?.message ?? "La petición ha caducado. Vuelve a conectar desde Claude.",
        });
      // Ya dio permiso antes a este cliente: Supabase devuelve directamente la vuelta.
      if (!("authorization_id" in data)) return window.location.assign(data.redirect_url);
      if (!vueltaPermitida(data.redirect_uri, import.meta.env.DEV)) {
        await supabase.auth.oauth.denyAuthorization(autorizacion, { skipBrowserRedirect: true });
        return setEstado({
          paso: "rechazado",
          motivo: `Solo se permite conectar la app de Claude, no ${data.redirect_uri}.`,
        });
      }
      setEstado({
        paso: "pedir",
        cliente: data.client.name || "Claude",
        email: data.user.email,
        destino: new URL(data.redirect_uri).host,
      });
    },
    [autorizacion],
  );

  useEffect(() => {
    authRepository.getViewer().then(cargar, () => setEstado({ paso: "entrar" }));
  }, [cargar]);

  async function decidir(permitir: boolean) {
    if (!autorizacion) return;
    setEnviando(true);
    const accion = permitir
      ? supabase.auth.oauth.approveAuthorization
      : supabase.auth.oauth.denyAuthorization;
    const { data, error } = await accion.call(supabase.auth.oauth, autorizacion, {
      skipBrowserRedirect: true,
    });
    if (error || !data) {
      setEnviando(false);
      return setEstado({ paso: "error", motivo: error?.message ?? "No se pudo completar." });
    }
    window.location.assign(data.redirect_url);
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bone px-5 py-12">
      <div className="w-full max-w-md">
        <BrandLogo className="mb-12 h-6 w-auto" />
        <p className="eyebrow text-primary">Conector del CRM</p>
        {estado.paso === "cargando" && <p className="mt-6 text-muted-foreground">Comprobando…</p>}
        {estado.paso === "entrar" && <Entrar alEntrar={cargar} />}
        {estado.paso === "pedir" && (
          <>
            <h1 className="mt-4 font-display text-5xl leading-[0.9]">
              ¿Dejas entrar a {estado.cliente}?
            </h1>
            <p className="mt-4 text-muted-foreground">
              Podrá ver tus leads, la agenda, las bodas y los cobros, y apuntar llamadas, mover
              etapas, crear leads, marcar pagos y crear enlaces de propuesta. Entra como{" "}
              <strong>{estado.email}</strong>. Lo puedes quitar cuando quieras desde los conectores
              de Claude.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Al aceptar vuelves a <strong>{estado.destino}</strong>.
            </p>
            <div className="mt-9 grid gap-3">
              <button
                className="btn-primary w-full"
                disabled={enviando}
                onClick={() => decidir(true)}
              >
                {enviando ? "Un momento…" : "Permitir"}
              </button>
              <button
                className="w-full py-3 text-sm underline"
                disabled={enviando}
                onClick={() => decidir(false)}
              >
                No permitir
              </button>
            </div>
          </>
        )}
        {(estado.paso === "rechazado" || estado.paso === "error") && (
          <>
            <h1 className="mt-4 font-display text-5xl leading-[0.9]">No se ha conectado.</h1>
            <p className="mt-4 text-muted-foreground">{estado.motivo}</p>
          </>
        )}
      </div>
    </main>
  );
}

function Entrar({ alEntrar }: { alEntrar: (viewer: Viewer) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fallo, setFallo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function entrar(evento: React.FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setFallo(null);
    try {
      alEntrar(await authRepository.signIn(email, password));
    } catch (error) {
      setFallo(error instanceof Error ? error.message : "No se pudo iniciar sesión");
      setEnviando(false);
    }
  }

  return (
    <>
      <h1 className="mt-4 font-display text-5xl leading-[0.9]">Entra con tu cuenta del CRM.</h1>
      <form className="mt-9 space-y-5" onSubmit={entrar}>
        <label className="block">
          <span className="eyebrow mb-2 block text-foreground">Email</span>
          <span className="relative block">
            <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="field pl-10"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </span>
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block text-foreground">Contraseña</span>
          <span className="relative block">
            <KeyRound className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className="field pl-10"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </span>
        </label>
        {fallo && <p className="text-sm text-destructive">{fallo}</p>}
        <button className="btn-primary w-full" disabled={enviando}>
          {enviando ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </>
  );
}
